import { useState } from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  Button,
  Box,
  InlineStack,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  PlusIcon,
  XIcon,
  PlusCircleIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";

import { Instructor } from "@features/events/types";
import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useParams } from "react-router-dom";
import type { Brand } from "@features/events/types";
import AssignBrandsModal from "@features/events/components/AssignBrandsModal";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";
import emptyBrandsImage from "@shared/assets/empty-states/empty-brands.svg";

interface AffiliatedBrandsCardProps {
  instructorData: Partial<Instructor>;
  onDataChange: (updates: Partial<Instructor>) => void;
}

interface BrandItemProps {
  brand: Brand;
  onRemove: (brandId: string) => void;
}

const BrandItem: React.FC<BrandItemProps> = ({
  brand,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${getLocalizedText(brand.name) || "brand"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(brand.name) || "brand"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <EventsDefaultAvatar
              name={brand.name}
              singleImage={brand.logoSquare}
            />
            <BlockStack gap="100">
              <Text as="span" variant="bodyMd" fontWeight="medium">
                {getLocalizedText(brand.name)}
              </Text>
              <Box maxWidth="100%">
                <Text as="span" variant="bodySm" tone="subdued" truncate>
                  {getLocalizedText(brand.headline)}
                </Text>
              </Box>
            </BlockStack>
          </InlineStack>

          <InlineStack
            gap="300"
            align="space-between"
            blockAlign="center"
            wrap={false}
          >
            <Text as="p" variant="bodyMd" tone="subdued">
              {getLocalizedText(brand.geoAddress?.summary)}
            </Text>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={XIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(brand.id)}
                  accessibilityLabel={t(
                    "instructorDetails.affiliatedBrands.removeBrand"
                  )}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

export default function AffiliatedBrandsCard({
  instructorData,
  onDataChange,
}: AffiliatedBrandsCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const brandIds = instructorData.brandIds ?? [];

  // Fetch all brands to get brand details for assigned brandIds
  const { data: allBrands = [] } = useQuery<Brand[]>({
    queryKey: ["brands", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/brands`),
    enabled: Boolean(hcc),
  });

  // Find brand objects from allBrands based on current brandIds
  const assignedBrands = brandIds
    .map((brandId) => allBrands.find((brand) => brand.id === brandId))
    .filter((brand): brand is Brand => brand !== undefined);

  const handleAddBrands = () => {
    setIsModalOpen(true);
  };

  const handleAssignBrands = (newBrandIds: string[]) => {
    onDataChange({ brandIds: newBrandIds });
    setIsModalOpen(false);
  };

  const handleRemoveBrand = (brandId: string) => {
    const updatedBrandIds = brandIds.filter((id: string) => id !== brandId);
    onDataChange({ brandIds: updatedBrandIds });
  };

  const renderBrandItem = (brand: Brand) => (
    <BrandItem
      brand={brand}
      onRemove={handleRemoveBrand}
    />
  );

  const hasBrands = assignedBrands.length > 0;

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("instructorDetails.affiliatedBrands.title")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("instructorDetails.affiliatedBrands.description")}
              </Text>
            </BlockStack>
          </InlineStack>

          {hasBrands ? (
            <BlockStack gap="200">
              <Box
                borderColor="border"
                borderWidth="025"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={assignedBrands}
                  onChange={(reorderedBrands) => {
                    const reorderedBrandIds = reorderedBrands.map(
                      (brand) => brand.id
                    );
                    onDataChange({ brandIds: reorderedBrandIds });
                  }}
                  idForItem={(brand) => brand?.id}
                  renderItem={(item) => renderBrandItem(item)}
                  showDividers={true}
                />
              </Box>
              {hasBrands && (
                <InlineStack>
                  <Button
                    variant="plain"
                    icon={PlusCircleIcon}
                    onClick={handleAddBrands}
                  >
                    {t("instructorDetails.affiliatedBrands.addButton")}
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("instructorDetails.affiliatedBrands.emptyState")}
              action={{
                content: t("instructorDetails.affiliatedBrands.addButton"),
                icon: PlusIcon,
                onAction: handleAddBrands,
              }}
              image={emptyBrandsImage}
            />
          )}
        </BlockStack>
      </Card>

      <AssignBrandsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedBrandIds={brandIds}
        onAssign={handleAssignBrands}
      />
    </>
  );
}
